import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n";

const NAV_LINKS = [
    { href: "#hero", key: "home" },
    { href: "#consulenza", key: "consulenza" },
    { href: "#esperienza", key: "esperienza" },
    { href: "#casi", key: "progetti" },
];

function LangToggle({ testidPrefix }) {
    const { lang, setLang } = useLanguage();
    return (
        <div className="flex items-center border border-navy-600" data-testid={`${testidPrefix}-lang-toggle`}>
            {["it", "en"].map((l) => (
                <button
                    key={l}
                    data-testid={`${testidPrefix}-lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors ${
                        lang === l ? "bg-sky-400 text-white" : "text-slate-400 hover:text-sky-400"
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { t } = useLanguage();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="main-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
                scrolled || open
                    ? "border-b border-navy-600 bg-navy-950/95 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
                <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-links">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            data-testid={`nav-link-${l.key}`}
                            className="text-xl font-medium text-slate-300 transition-colors hover:text-sky-400 xl:text-2xl"
                        >
                            {t.nav[l.key]}
                        </a>
                    ))}
                    <a
                        href="/contatti"
                        data-testid="nav-link-contatti-page"
                        className="text-xl font-medium text-slate-300 transition-colors hover:text-sky-400 xl:text-2xl"
                    >
                        {t.nav.contatti}
                    </a>
                </nav>

                <div className="hidden items-center gap-4 lg:flex">
                    <LangToggle testidPrefix="nav" />
                    <a
                        href="#contatti"
                        data-testid="nav-cta"
                        className="inline-flex items-center gap-1.5 bg-sky-400 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-sky-500"
                    >
                        {t.nav.cta}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="text-slate-50 lg:hidden"
                    aria-label="Apri menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {open && (
                <div className="border-t border-navy-600 bg-navy-950 px-6 py-6 lg:hidden" data-testid="nav-mobile-menu">
                    <div className="flex flex-col gap-4">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.key}
                                href={l.href}
                                data-testid={`nav-mobile-link-${l.key}`}
                                onClick={() => setOpen(false)}
                                className="text-lg font-medium text-slate-300 transition-colors hover:text-sky-400"
                            >
                                {t.nav[l.key]}
                            </a>
                        ))}
                        <a
                            href="/contatti"
                            data-testid="nav-mobile-link-contatti-page"
                            onClick={() => setOpen(false)}
                            className="text-lg font-medium text-slate-300 transition-colors hover:text-sky-400"
                        >
                            {t.nav.contatti}
                        </a>
                        <div className="mt-2 flex items-center gap-4">
                            <LangToggle testidPrefix="nav-mobile" />
                            <a
                                href="#contatti"
                                data-testid="nav-mobile-cta"
                                onClick={() => setOpen(false)}
                                className="inline-flex w-fit items-center gap-2 bg-sky-400 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white"
                            >
                                {t.nav.cta}
                                <ArrowUpRight className="h-3.5 w-3.5" />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
